import React, {Fragment, useCallback, useEffect} from "react";
import {connect, useSelector} from "react-redux";
import {useNavigate, useParams} from "react-router-dom";
import ModulesPlaceholder from "../components/modules-placeholder";
import AgentsManager from "../components/package-manager/AgentsManager";
import EasyWmsLoading from "../components/package-manager/easy-wms-loading";
import PackageLicensesManager from "../components/package-manager/PackageLicensesManager";
import ResourceLicensesManager from "../components/package-manager/ResourceLicensesManager";
import ThemesManager from "../components/package-manager/ThemesManager";
import SearchDialog from "../components/search-dialog";
import {getNotFoundPageInternalUrl, getPackagesSearchInternalUrl} from "../constants/appRoutes";
import Call from "../hocs/call";
import {useHasFeature} from "../hooks/useHasFeature";
import {USER_ERRORS_NOT_ACCESSIBLE_NODE} from "../translations/getManagedErrorsTranslations";
import {fetchNodeAiFeatures} from "../state/ai-assistant/aiAssistantAction";
import {aiEnabledFeaturesSelector} from "../state/ai-assistant/aiAssistantSelector";
import {LICENSE_FEATURES_AI} from "../state/app/contants";
import {clearCatalog, fetchCatalog} from "../state/catalog/catalogActions";
import {externalServicesSelector} from "../state/config/configSelectors";
import {clearNode, fetchNode, fetchNodeItemContainers} from "../state/node/nodeActions";
import {showUserLoginForm} from "../state/user/userActions";
import {getIsUserAuthenticated} from "../utils/permissions";

const mapStateToProps = state => ({
  hub: state.hub,
  node: state.node,
  catalog: state.catalog,
  user: state.user,
  geoglossaryBaseUrl: state.config.externalServices?.geoglossary,
  isMultiViewerTheme: state.themeConfig.isMultiViewer,
  moduleConfigs: state.moduleConfigs
});

const mapDispatchToProps = dispatch => ({
  fetchNode: ({nodeId, nodeCode}) => dispatch(fetchNode(nodeId, nodeCode)),
  clearNode: () => dispatch(clearNode()),
  fetchCatalog: ({nodeId, nodeCode, baseUrl}) => dispatch(fetchCatalog(nodeId, nodeCode, baseUrl)),
  clearCatalog: () => dispatch(clearCatalog()),
  onLoginShow: () => dispatch(showUserLoginForm(USER_ERRORS_NOT_ACCESSIBLE_NODE, "warning", true)),
  fetchNodeDashboards: nodeId => dispatch(fetchNodeItemContainers(nodeId, "dashboard")),
  fetchNodeCustomPages: nodeId => dispatch(fetchNodeItemContainers(nodeId, "customPages")),
  fetchAiFeatures: (nodeId, baseUrl) => dispatch(fetchNodeAiFeatures(nodeId, baseUrl))
});

const NodeDomain = ({
  nodeCode: externalNodeCode,
  hub,
  node,
  catalog,
  user,
  fetchNode,
  clearNode,
  fetchCatalog,
  clearCatalog,
  fetchNodeDashboards,
  fetchNodeCustomPages,
  fetchAiFeatures,
  onLoginShow,
  children,
  geoglossaryBaseUrl,
  isMultiViewerTheme,
  moduleConfigs
}) => {
  const {nodeCode: urlNodeCode} = useParams();
  const navigate = useNavigate();

  const isAiEnabled = useHasFeature(LICENSE_FEATURES_AI);
  const aiAssistantBaseUrl = useSelector(externalServicesSelector)?.ai;
  const aiEnabledFeatures = useSelector(aiEnabledFeaturesSelector);

  const nodeCode = (externalNodeCode ?? urlNodeCode ?? "").toLowerCase();

  useEffect(() => {
    return () => {
      clearNode();
      clearCatalog();
    };
  }, [nodeCode, clearNode, clearCatalog]);

  const nodeHavingCode = hub.nodes.find(({code}) => code.toLowerCase() === nodeCode) || null;

  const isFetchNodeEnabled = !node || node.code.toLowerCase() !== nodeCode;
  const fetchCatalogBaseUrl = isMultiViewerTheme && geoglossaryBaseUrl ? geoglossaryBaseUrl : undefined;

  const fetchNodeConfigs = useCallback(() => {
    if (node !== null) {
      // node dashboards
      if (moduleConfigs.modules.includes("dashboard") && !node.itemContainers?.dashboard) {
        fetchNodeDashboards(node.nodeId);
      }

      // node custom page
      if (moduleConfigs.modules.includes("custom-page") && !node.itemContainers?.customPages) {
        fetchNodeCustomPages(node.nodeId);
      }

      // ai config
      if (isAiEnabled && aiAssistantBaseUrl && !aiEnabledFeatures) {
        fetchAiFeatures(node.nodeId, aiAssistantBaseUrl);
      }
    }
  }, [
    node,
    moduleConfigs,
    aiAssistantBaseUrl,
    isAiEnabled,
    aiEnabledFeatures,
    fetchNodeDashboards,
    fetchNodeCustomPages,
    fetchAiFeatures
  ]);

  useEffect(() => {
    if (!nodeHavingCode) {
      if (!getIsUserAuthenticated(user)) {
        if (!user.isLoginDialogOpen) {
          onLoginShow();
        }
      } else {
        navigate(getNotFoundPageInternalUrl());
      }
    }
  }, [nodeHavingCode, user, onLoginShow, navigate]);

  return (
    <Fragment>
      {nodeHavingCode !== null && (
        <Call
          cb={fetchNode}
          cbParam={{nodeId: nodeHavingCode.nodeId, nodeCode: nodeCode}}
          disabled={!nodeCode || !isFetchNodeEnabled || node !== null}
        >
          <Call
            cb={fetchCatalog}
            cbParam={{nodeId: node?.nodeId, nodeCode: node?.code, baseUrl: fetchCatalogBaseUrl}}
            disabled={node === null || catalog !== null}
          >
            <Call cb={fetchNodeConfigs} disabled={node === null || catalog === null}>
              <div id={`node__${nodeCode}`} style={{width: "100%", height: "100%"}}>
                <Fragment key={nodeCode}>
                  {children}
                  <ModulesPlaceholder id="node-domain-placeholder" />
                </Fragment>
              </div>
            </Call>
          </Call>
        </Call>
      )}
      <SearchDialog
        modalWidth={320}
        dialogTop={56}
        onSubmit={value => navigate(getPackagesSearchInternalUrl(node.code, value))}
      />
      <EasyWmsLoading />
      <AgentsManager />
      <ThemesManager />
      <PackageLicensesManager />
      <ResourceLicensesManager />
    </Fragment>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(NodeDomain);
